import { debounce } from '@theme/utilities';

/**
 * Decode HTML entities in strings from data-* attributes (e.g. &quot; → ").
 * @param {string} str
 * @returns {string}
 */
function decodeHtmlEntities(str) {
  if (!str) return '';
  const el = document.createElement('textarea');
  el.innerHTML = str;
  return el.value;
}

/**
 * @param {string} template
 * @param {string} terms
 */
function applyTermsTemplate(template, terms) {
  return decodeHtmlEntities(template).replace(/__TERMS__/g, terms);
}

/**
 * Build suggest.json URL (locale-aware via Shopify.routes.root).
 * @param {string} query
 * @returns {string}
 */
function buildSuggestArticleUrl(query) {
  const root = window.Shopify?.routes?.root;
  const normalizedRoot = root && typeof root === 'string' ? root : '/';
  const prefix = normalizedRoot.endsWith('/') ? normalizedRoot : `${normalizedRoot}/`;
  const url = new URL(`${prefix}search/suggest.json`, window.location.origin);
  url.searchParams.set('q', query);
  url.searchParams.set('resources[type]', 'article');
  url.searchParams.set('resources[limit]', '10');
  url.searchParams.set('resources[limit_scope]', 'each');
  return url.toString();
}

/**
 * @param {string} html
 * @returns {string}
 */
function stripHtml(html) {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent?.trim() ?? '';
}

/**
 * @param {string} str
 * @param {number} max
 */
function truncateText(str, max) {
  if (str.length <= max) return str;
  return `${str.slice(0, max).trim()}…`;
}

/**
 * @param {Record<string, unknown>} article
 */
function createArticleCard(article) {
  const title = typeof article.title === 'string' ? article.title : '';
  const url = typeof article.url === 'string' ? article.url : '#';
  const imageUrl = typeof article.image === 'string' ? article.image : '';
  const excerpt = truncateText(stripHtml(article.summary_html || article.body || ''), 160);

  const wrap = document.createElement('div');
  wrap.className = 'resource-card';
  wrap.setAttribute('data-resource-type', 'article');

  const link = document.createElement('a');
  link.className = 'resource-card__link';
  link.href = url;
  const vh = document.createElement('span');
  vh.className = 'visually-hidden';
  vh.textContent = title;
  link.appendChild(vh);

  const media = document.createElement('div');
  media.className = 'resource-card__media';
  media.style.setProperty('--resource-card-aspect-ratio', '1.5');

  if (imageUrl) {
    const img = document.createElement('img');
    img.className = 'resource-card__image';
    img.src = imageUrl;
    img.alt = '';
    img.loading = 'lazy';
    media.appendChild(img);
  } else {
    const ph = document.createElement('div');
    ph.className = 'resource-card__image-placeholder';
    ph.setAttribute('aria-hidden', 'true');
    ph.textContent = truncateText(title, 60);
    media.appendChild(ph);
  }

  const content = document.createElement('div');
  content.className = 'resource-card__content';

  const titleP = document.createElement('p');
  titleP.className = 'resource-card__title paragraph';
  titleP.textContent = title;

  const sub = document.createElement('p');
  sub.className = 'resource-card__subtext paragraph';
  sub.textContent = excerpt;

  content.appendChild(titleP);
  content.appendChild(sub);

  wrap.appendChild(link);
  wrap.appendChild(media);
  wrap.appendChild(content);

  return wrap;
}

/**
 * @param {unknown[]} articles
 * @param {HTMLElement} target
 * @param {string} emptyMessage
 */
function renderArticleResults(articles, target, emptyMessage) {
  target.innerHTML = '';

  if (!Array.isArray(articles) || articles.length === 0) {
    const wrap = document.createElement('div');
    wrap.className = 'search-results__empty-articles';
    const p = document.createElement('p');
    p.textContent = emptyMessage;
    wrap.appendChild(p);
    target.appendChild(wrap);
    return;
  }

  const ul = document.createElement('ul');
  ul.className = 'search-results-articles__grid';
  ul.setAttribute('role', 'list');

  for (const article of articles) {
    const li = document.createElement('li');
    li.className = 'search-results-articles__item';
    li.setAttribute('role', 'listitem');
    li.appendChild(createArticleCard(article));
    ul.appendChild(li);
  }

  target.appendChild(ul);
}

function initBlogLiveSearch() {
  const liveRoot = document.querySelector('[data-blog-live-search]');
  const form = document.querySelector('[data-blog-search-form]');
  const input = document.querySelector('[data-blog-search-input]');
  const defaultBlock = document.querySelector('[data-blog-default-posts]');

  if (!liveRoot || !form || !input || !defaultBlock) return;

  const headingEl = liveRoot.querySelector('[data-blog-live-heading]');
  const resultsEl = liveRoot.querySelector('[data-blog-live-results]');
  const statusEl = liveRoot.querySelector('[data-blog-live-status]');
  const headingTemplate = liveRoot.dataset.headingTemplate || '';
  const emptyTemplate = liveRoot.dataset.emptyTemplate || '';
  const searchButton = document.querySelector('[data-blog-search-button]');

  /** @type {AbortController | null} */
  let activeFetch = null;

  /**
   * @param {boolean} searching
   */
  function setStatus(searching) {
    if (!statusEl) return;
    if (searching) {
      statusEl.hidden = false;
      statusEl.textContent = 'Searching…';
    } else {
      statusEl.hidden = true;
      statusEl.textContent = '';
    }
  }

  /**
   * @param {boolean} active - true when user is filtering by query
   */
  function setSearchMode(active) {
    liveRoot.hidden = !active;
    defaultBlock.hidden = active;

    if (!active && resultsEl) {
      resultsEl.innerHTML = '';
    }
    if (!active && headingEl) {
      headingEl.textContent = '';
    }
    setStatus(false);
  }

  /**
   * @param {string} query
   */
  async function runSearch(query) {
    const q = query.trim();

    if (!q.length) {
      setSearchMode(false);
      return;
    }

    setSearchMode(true);
    if (headingEl && headingTemplate) {
      headingEl.textContent = applyTermsTemplate(headingTemplate, q);
    }

    activeFetch?.abort();
    const controller = new AbortController();
    activeFetch = controller;

    setStatus(true);

    const fetchUrl = buildSuggestArticleUrl(q);

    try {
      const response = await fetch(fetchUrl, {
        signal: controller.signal,
        headers: { Accept: 'application/json' },
      });

      const data = await response.json().catch(() => ({}));

      if (controller.signal.aborted || !resultsEl) return;

      if (!response.ok) {
        const msg =
          typeof data?.description === 'string'
            ? data.description
            : `Search request failed (${response.status}).`;
        renderArticleResults([], resultsEl, msg);
        return;
      }

      const articles = data?.resources?.results?.articles ?? [];

      const emptyMsg = emptyTemplate ? applyTermsTemplate(emptyTemplate, q) : `No results found for "${q}".`;

      renderArticleResults(articles, resultsEl, emptyMsg);
    } catch (e) {
      if (e?.name === 'AbortError') return;
      if (resultsEl) {
        renderArticleResults(
          [],
          resultsEl,
          'Something went wrong. Try again.'
        );
      }
    } finally {
      if (!controller.signal.aborted) {
        setStatus(false);
      }
    }
  }

  const debouncedSearch = debounce(() => {
    runSearch(input.value);
  }, 300);

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    debouncedSearch.cancel();
    runSearch(input.value);
  });

  searchButton?.addEventListener('click', () => {
    debouncedSearch.cancel();
    runSearch(input.value);
  });

  input.addEventListener('input', (e) => {
    const v = /** @type {HTMLInputElement} */ (e.target).value;
    if (!v.trim().length) {
      debouncedSearch.cancel();
      activeFetch?.abort();
      setSearchMode(false);
      return;
    }
    debouncedSearch();
  });

  input.addEventListener('search', () => {
    if (!input.value.trim().length) {
      debouncedSearch.cancel();
      activeFetch?.abort();
      setSearchMode(false);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBlogLiveSearch);
} else {
  initBlogLiveSearch();
}
